import { useState, useMemo, useCallback } from 'react';
import { useOrderNetting, NettingOrder } from '../hooks/useOrderNetting';
import NettingSuccessModal from './NettingSuccessModal';
import { usePaywallGate } from '../../../utils/paywall';
import { roundMoney } from '../../../utils/paymentAmount';

type NettingMode = 'order' | 'amount';

function fmt(n: number) {
  return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function OrderRow({
  order,
  selected,
  onToggle,
}: {
  order: NettingOrder;
  selected: boolean;
  onToggle: (id: string) => void;
}) {
  return (
    <label
      className={`flex items-start gap-2.5 p-2.5 rounded-lg cursor-pointer transition-colors border ${
        selected ? 'bg-teal-50 border-teal-200' : 'bg-gray-50 border-transparent hover:bg-gray-100'
      }`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onToggle(order.id)}
        className="mt-0.5 accent-teal-500 w-3.5 h-3.5 shrink-0 cursor-pointer"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1">
          <span className="text-xs font-medium text-gray-800 truncate">
            {order.orderNumber || order.id.slice(0, 8)}
          </span>
          <span className="text-xs font-semibold text-gray-900 whitespace-nowrap">
            ¥{fmt(order.orderAmount)}
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          {order.serviceTypeName && (
            <span className="text-[10px] text-gray-400 truncate">{order.serviceTypeName}</span>
          )}
          {order.orderStartDate && (
            <span className="text-[10px] text-gray-400 whitespace-nowrap">{order.orderStartDate}</span>
          )}
        </div>
        {order.paidAmount > 0 && (
          <div className="flex items-center justify-between mt-0.5">
            <span className="text-[10px] text-amber-500">已付 ¥{fmt(order.paidAmount)}</span>
            <span className="text-[10px] text-rose-500 font-medium">待付 ¥{fmt(order.unpaidAmount)}</span>
          </div>
        )}
        {order.paidAmount === 0 && (
          <span className="text-[10px] text-rose-500 font-medium">待付 ¥{fmt(order.unpaidAmount)}</span>
        )}
      </div>
    </label>
  );
}

function EmptyCol({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-gray-300">
      <i className={`${label === '收入' ? 'ri-arrow-down-circle-line' : 'ri-arrow-up-circle-line'} text-3xl mb-1`} />
      <p className="text-xs">暂无{label}订单</p>
    </div>
  );
}

export default function OrderNetting() {
  const { groups, loading, error, refresh, executeNetting } = useOrderNetting();
  const gate = usePaywallGate();

  const [mode, setMode] = useState<NettingMode>('order');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [selectedIncome, setSelectedIncome] = useState<Set<string>>(new Set());
  const [selectedExpense, setSelectedExpense] = useState<Set<string>>(new Set());
  const [scopeFilter, setScopeFilter] = useState<'all' | 'unpaid' | 'partial'>('all');
  const [search, setSearch] = useState('');
  const [executing, setExecuting] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [nettingResult, setNettingResult] = useState<{
    batchNo: string;
    nettingAmount: number;
    mode: 'order' | 'amount';
    customerName: string;
    needsApproval: boolean;
    nettingLogId: string;
    orderCount: number;
  } | null>(null);

  const showToast = useCallback((type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const filteredGroups = useMemo(() => {
    let list = groups;
    if (search.trim()) {
      const kw = search.trim().toLowerCase();
      list = list.filter(g => g.customerName.toLowerCase().includes(kw));
    }
    return list;
  }, [groups, search]);

  const currentGroup = useMemo(
    () => filteredGroups.find(g => g.customerId === selectedCustomerId) || null,
    [filteredGroups, selectedCustomerId]
  );

  const filterOrders = useCallback((orders: NettingOrder[]) => {
    // 与服务端「未付款」筛选同口径：0 元单没有任何欠款，不进结算候选
    if (scopeFilter === 'unpaid') return orders.filter(o => o.paymentStatus === '未付款' && Number(o.orderAmount ?? 0) > 0);
    if (scopeFilter === 'partial') return orders.filter(o => o.paymentStatus === '部分付款');
    return orders;
  }, [scopeFilter]);

  const incomeOrders = useMemo(() => filterOrders(currentGroup?.incomeOrders ?? []), [currentGroup, filterOrders]);
  const expenseOrders = useMemo(() => filterOrders(currentGroup?.expenseOrders ?? []), [currentGroup, filterOrders]);

  const selectedIncomeOrders = useMemo(
    () => incomeOrders.filter(o => selectedIncome.has(o.id)),
    [incomeOrders, selectedIncome]
  );
  const selectedExpenseOrders = useMemo(
    () => expenseOrders.filter(o => selectedExpense.has(o.id)),
    [expenseOrders, selectedExpense]
  );

  // 归整必须发生在求和这一步：nettingAmount 不只用于展示，还直接作为上送值发给后端，
  // 而 float reduce 必然带尾数（66.67000000000002），后端 @MaxTwoDecimals 闸门会把它 400 掉。
  const totalSelectedIncome = useMemo(
    () => roundMoney(selectedIncomeOrders.reduce((s, o) => s + o.unpaidAmount, 0)),
    [selectedIncomeOrders]
  );
  const totalSelectedExpense = useMemo(
    () => roundMoney(selectedExpenseOrders.reduce((s, o) => s + o.unpaidAmount, 0)),
    [selectedExpenseOrders]
  );

  const nettingAmount = useMemo(() => Math.min(totalSelectedIncome, totalSelectedExpense), [totalSelectedIncome, totalSelectedExpense]);

  const canExecute = useMemo(() => {
    if (selectedIncomeOrders.length === 0 || selectedExpenseOrders.length === 0) return false;
    if (mode === 'order') {
      return Math.abs(totalSelectedIncome - totalSelectedExpense) < 0.01;
    }
    return nettingAmount > 0;
  }, [mode, selectedIncomeOrders, selectedExpenseOrders, totalSelectedIncome, totalSelectedExpense, nettingAmount]);

  const modeWarning = useMemo(() => {
    if (mode === 'order' && selectedIncomeOrders.length > 0 && selectedExpenseOrders.length > 0) {
      const diff = Math.abs(totalSelectedIncome - totalSelectedExpense);
      if (diff > 0.01) return `收入与支出金额不等（差额 ¥${fmt(diff)}），订单抵消要求双方金额相同`;
    }
    return null;
  }, [mode, selectedIncomeOrders, selectedExpenseOrders, totalSelectedIncome, totalSelectedExpense]);

  const toggleIncome = useCallback((id: string) => {
    setSelectedIncome(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleExpense = useCallback((id: string) => {
    setSelectedExpense(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIncome(new Set());
    setSelectedExpense(new Set());
  }, []);

  const handleCustomerChange = useCallback((cid: string) => {
    setSelectedCustomerId(cid);
    setSelectedIncome(new Set());
    setSelectedExpense(new Set());
  }, []);

  const handleExecute = useCallback(async () => {
    if (!canExecute) return;
    // 与后端 @RequireFeature('order_offset') 对齐：先给出升级指引，
    // 否则免费用户点完只会看到一条 403 网络错误，不知道要去哪里解锁
    if (!(await gate('orderOffset', '订单抵消'))) return;
    const allIds = [...selectedIncomeOrders.map(o => o.id), ...selectedExpenseOrders.map(o => o.id)];
    const customerInfo = currentGroup
      ? { id: currentGroup.customerId, name: currentGroup.customerName }
      : undefined;
    setExecuting(true);
    const result = await executeNetting(allIds, nettingAmount, mode, customerInfo);
    setExecuting(false);
    if (result.success) {
      // 需要审核时显示成功弹窗（含分享/导出功能）；不需要审核时显示简单 toast
      if (result.needsApproval) {
        setNettingResult({
          batchNo: result.batchNo || '',
          nettingAmount,
          mode,
          customerName: currentGroup?.customerName || '',
          needsApproval: true,
          nettingLogId: result.nettingLogId || '',
          orderCount: allIds.length,
        });
      } else {
        showToast('success', result.message);
      }
      clearSelection();
      setSelectedCustomerId('');
    } else {
      showToast('error', result.message);
    }
  }, [canExecute, gate, selectedIncomeOrders, selectedExpenseOrders, nettingAmount, mode, currentGroup, executeNetting, showToast, clearSelection]);

  return (
    <div id="order-netting-section" className="bg-white border border-gray-100 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 md:px-5 py-3.5 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 flex items-center justify-center bg-teal-50 rounded-lg">
            <i className="ri-exchange-dollar-line text-teal-600 text-sm" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">订单抵消</h3>
            <p className="text-[10px] text-gray-400 mt-0.5">双向订单费用对冲 · 年度结算工具</p>
          </div>
          {!loading && groups.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-medium rounded-full border border-amber-100">
              {groups.length} 位客户可抵消
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); refresh(); }}
            className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-500 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line text-sm" />
          </button>
        </div>
      </div>

      <div className="p-4 md:p-5">
        {loading && (
          <div className="flex items-center justify-center py-10 text-gray-400">
            <i className="ri-loader-4-line animate-spin mr-2" />
            <span className="text-sm">加载中…</span>
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-8 text-rose-400 text-sm">{error}</div>
        )}

        {!loading && !error && groups.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400">
            <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-xl mb-3">
              <i className="ri-exchange-dollar-line text-xl" />
            </div>
            <p className="text-sm font-medium text-gray-500">暂无可抵消订单</p>
            <p className="text-xs text-gray-400 mt-1">当客户同时存在未结清的收入和支出订单时，将在此显示</p>
          </div>
        )}

        {!loading && !error && groups.length > 0 && (
          <div className="space-y-4">
            {/* Controls bar */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Mode toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                {([
                  { value: 'order', label: '订单抵消' },
                  { value: 'amount', label: '金额抵消' },
                ] as { value: NettingMode; label: string }[]).map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setMode(opt.value); clearSelection(); }}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                      mode === opt.value
                        ? 'bg-white text-teal-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Scope filter */}
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                {([
                  { value: 'all', label: '全部未结清' },
                  { value: 'unpaid', label: '未付款（有欠款）' },
                  { value: 'partial', label: '部分付款' },
                ] as { value: 'all' | 'unpaid' | 'partial'; label: string }[]).map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setScopeFilter(opt.value); clearSelection(); }}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                      scopeFilter === opt.value
                        ? 'bg-white text-teal-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Customer search */}
              <div className="relative flex-1 min-w-[140px]">
                <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                <input
                  type="text"
                  placeholder="搜索客户名称"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-7 pr-3 py-1.5 text-xs bg-gray-100 border-0 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-teal-400"
                />
              </div>

              {/* Customer selector */}
              <select
                value={selectedCustomerId}
                onChange={e => handleCustomerChange(e.target.value)}
                className="px-2.5 py-1.5 text-xs bg-gray-100 border-0 rounded-lg text-gray-700 focus:outline-none focus:ring-1 focus:ring-teal-400 cursor-pointer max-w-[180px]"
              >
                <option value="">选择客户</option>
                {filteredGroups.map(g => (
                  <option key={g.customerId} value={g.customerId}>
                    {g.customerName}（¥{fmt(g.nettableAmount)}）
                  </option>
                ))}
              </select>
            </div>

            {/* Mode description */}
            <div className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${mode === 'order' ? 'bg-teal-50 text-teal-700' : 'bg-amber-50 text-amber-700'}`}>
              <i className={`${mode === 'order' ? 'ri-scales-line' : 'ri-funds-line'} text-sm mt-0.5 shrink-0`} />
              <span>
                {mode === 'order'
                  ? '订单抵消：选择收入与支出方各一笔金额相同的订单进行等额抵消，双方金额必须完全一致。'
                  : '金额抵消：分别选择收入和支出方的订单（可多选），系统将按双方待付金额较小值进行对冲，支持跨订单合并结算。'}
              </span>
            </div>

            {/* Order columns */}
            {selectedCustomerId ? (
              <div className="grid grid-cols-2 gap-3">
                {/* Income / Receivable */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                      <i className="ri-arrow-down-circle-line text-teal-500" />
                      应收款
                    </span>
                    <span className="text-xs text-gray-400">
                      {incomeOrders.length} 笔 · ¥{fmt(incomeOrders.reduce((s, o) => s + o.unpaidAmount, 0))}
                    </span>
                  </div>
                  <div className="space-y-1.5 max-h-56 overflow-y-auto pr-0.5">
                    {incomeOrders.length === 0 ? (
                      <EmptyCol label="收入" />
                    ) : (
                      incomeOrders.map(o => (
                        <OrderRow
                          key={o.id}
                          order={o}
                          selected={selectedIncome.has(o.id)}
                          onToggle={toggleIncome}
                        />
                      ))
                    )}
                  </div>
                  {selectedIncomeOrders.length > 0 && (
                    <div className="mt-2 flex items-center justify-between px-2.5 py-1.5 bg-teal-50 rounded-lg">
                      <span className="text-[10px] text-teal-600">已选 {selectedIncomeOrders.length} 笔</span>
                      <span className="text-xs font-semibold text-teal-700">¥{fmt(totalSelectedIncome)}</span>
                    </div>
                  )}
                </div>

                {/* Expense / Payable */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                      <i className="ri-arrow-up-circle-line text-rose-500" />
                      应付款
                    </span>
                    <span className="text-xs text-gray-400">
                      {expenseOrders.length} 笔 · ¥{fmt(expenseOrders.reduce((s, o) => s + o.unpaidAmount, 0))}
                    </span>
                  </div>
                  <div className="space-y-1.5 max-h-56 overflow-y-auto pr-0.5">
                    {expenseOrders.length === 0 ? (
                      <EmptyCol label="支出" />
                    ) : (
                      expenseOrders.map(o => (
                        <OrderRow
                          key={o.id}
                          order={o}
                          selected={selectedExpense.has(o.id)}
                          onToggle={toggleExpense}
                        />
                      ))
                    )}
                  </div>
                  {selectedExpenseOrders.length > 0 && (
                    <div className="mt-2 flex items-center justify-between px-2.5 py-1.5 bg-rose-50 rounded-lg">
                      <span className="text-[10px] text-rose-600">已选 {selectedExpenseOrders.length} 笔</span>
                      <span className="text-xs font-semibold text-rose-700">¥{fmt(totalSelectedExpense)}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Customer overview list */
              <div className="space-y-2 max-h-60 overflow-y-auto">
                <p className="text-xs text-gray-400 mb-2">以下客户存在双向未结清订单，可选择客户进行对冲抵消：</p>
                {filteredGroups.map(g => (
                  <button
                    key={g.customerId}
                    onClick={() => handleCustomerChange(g.customerId)}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-teal-50 border border-transparent hover:border-teal-200 rounded-xl transition-colors cursor-pointer text-left group"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 flex items-center justify-center bg-white rounded-xl border border-gray-100 group-hover:border-teal-200 shrink-0">
                        <i className="ri-user-line text-gray-400 group-hover:text-teal-500 text-sm" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-800">{g.customerName}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          收入 {g.incomeOrders.length} 笔 · 支出 {g.expenseOrders.length} 笔
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-semibold text-teal-600">可抵 ¥{fmt(g.nettableAmount)}</p>
                      <div className="flex items-center gap-2 mt-0.5 justify-end">
                        <span className="text-[10px] text-gray-400">收 ¥{fmt(g.totalReceivable)}</span>
                        <span className="text-[10px] text-gray-300">|</span>
                        <span className="text-[10px] text-gray-400">付 ¥{fmt(g.totalPayable)}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Warning */}
            {modeWarning && (
              <div className="flex items-start gap-2 px-3 py-2 bg-rose-50 rounded-lg text-xs text-rose-600 border border-rose-100">
                <i className="ri-error-warning-line text-sm mt-0.5 shrink-0" />
                <span>{modeWarning}</span>
              </div>
            )}

            {/* Execute footer */}
            {selectedCustomerId && (selectedIncomeOrders.length > 0 || selectedExpenseOrders.length > 0) && (
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div className="space-y-0.5">
                  {mode === 'amount' && nettingAmount > 0 && (
                    <p className="text-xs text-gray-700">
                      本次抵消金额：
                      <span className="font-semibold text-teal-600 ml-1">¥{fmt(nettingAmount)}</span>
                    </p>
                  )}
                  {mode === 'order' && selectedIncomeOrders.length > 0 && selectedExpenseOrders.length > 0 && (
                    <p className="text-xs text-gray-700">
                      抵消订单：
                      <span className="font-semibold text-teal-600 ml-1">
                        {selectedIncomeOrders.length + selectedExpenseOrders.length} 笔 · ¥{fmt(Math.min(totalSelectedIncome, totalSelectedExpense))}
                      </span>
                    </p>
                  )}
                  <p className="text-[10px] text-gray-400">确认后，所选订单将标记为「已支付」并记录抵消方式</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={clearSelection}
                    className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    清空选择
                  </button>
                  <button
                    onClick={handleExecute}
                    disabled={!canExecute || executing}
                    className="px-4 py-1.5 text-xs font-medium text-white bg-teal-500 rounded-lg hover:bg-teal-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5"
                  >
                    {executing && <i className="ri-loader-4-line animate-spin" />}
                    {executing ? '执行中…' : '执行抵消'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* NettingSuccessModal */}
      {nettingResult && (
        <NettingSuccessModal
          result={nettingResult}
          onClose={() => setNettingResult(null)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${
          toast.type === 'success' ? 'bg-teal-500 text-white' : 'bg-rose-500 text-white'
        }`}>
          <i className={toast.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} />
          {toast.msg}
        </div>
      )}
    </div>
  );
}
